import { Alert, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { useStore } from 'src/stores';
import { UtilityPortalCredentialSection } from './index';
import { PendingCredentialFields } from './PendingCredentialFields';
import { UpgradeNotice } from './UpgradeNotice';
import { useBillAutomationEntitlement } from './useBillAutomationEntitlement';
import { isLivableStaffFromToken } from 'src/utils/isLivableStaffFromToken';

/**
 * What a saved utility shows for each outcome of its credential lookup.
 *
 * 'loading' renders nothing rather than an empty form, because "we have not asked yet" and "there
 * is no credential" look identical once the answer is null.
 */
const renderSavedState = (credentialState, props) => {
  if (credentialState === 'loading') return null;
  if (credentialState === 'unavailable') {
    return /*#__PURE__*/React.createElement(Alert, {
      severity: "warning",
      sx: {
        marginTop: '16px'
      }
    }, "Bill automation credentials could not be loaded, so this utility's credential state is unknown. Reload before adding or changing one.");
  }
  return /*#__PURE__*/React.createElement(UtilityPortalCredentialSection, props);
};

/**
 * With billing options on, a new utility links to a setup when it's saved, and its portal login goes under that setup
 * afterwards (LB-6191): with the account number now required, a login collected here could be refused after the utility
 * saved, and lose the password typed for it.
 */
const ADD_AFTER_SAVE = 'Add the portal login once the utility is saved.';

/** The ADD path: collect a login only for a company that can store one. */
const renderPendingState = (entitled, props) => {
  if (entitled === null) return null;
  if (!entitled) return /*#__PURE__*/React.createElement(UpgradeNotice, {
    isLivableStaff: isLivableStaffFromToken()
  });
  return /*#__PURE__*/React.createElement(PendingCredentialFields, props);
};

/**
 * The credential block, wherever a billing template is edited.
 *
 * Three modals render this. Keeping the role gate in one place is the point: a fourth call site that forgot it
 * would offer the form to a role the API refuses.
 *
 * `propertyUtilityId` decides the mode. Present means a saved utility that can own a credential
 * now; absent means the utility does not exist yet, so the fields are collected and saved after
 * creation, or, with billing options on, the login waits until the utility is saved.
 */
export const UtilityCredentialSlot = observer(_ref => {
  var _store$account;
  let canManageCredentials = _ref.canManageCredentials,
    isAutomated = _ref.isAutomated,
    className = _ref.className,
    companyId = _ref.companyId,
    propertyId = _ref.propertyId,
    propertyUtilityId = _ref.propertyUtilityId,
    utilityProviderId = _ref.utilityProviderId,
    utilityTypeId = _ref.utilityTypeId,
    providerName = _ref.providerName,
    credential = _ref.credential,
    _ref$credentialState = _ref.credentialState,
    credentialState = _ref$credentialState === void 0 ? 'loading' : _ref$credentialState,
    onChanged = _ref.onChanged,
    pendingRef = _ref.pendingRef;
  const store = useStore();
  const loginAfterSave = !!(store !== null && store !== void 0 && (_store$account = store.account) !== null && _store$account !== void 0 && (_store$account = _store$account.featureFlagsStore) !== null && _store$account !== void 0 && _store$account.getIsBillingOptionsEnabled) && !propertyUtilityId;
  // Before the role check returns, so hook order never changes between renders.
  const entitled = useBillAutomationEntitlement(canManageCredentials && !propertyUtilityId && !loginAfterSave ? companyId : null);
  if (!canManageCredentials) return null;
  if (loginAfterSave) {
    return /*#__PURE__*/React.createElement("div", {
      className: className
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "body2",
      color: "light",
      component: "p"
    }, ADD_AFTER_SAVE));
  }
  return /*#__PURE__*/React.createElement("div", {
    className: className
  }, propertyUtilityId ?
  // Only a lookup that came back decides what is shown. Both other states arrive here as
  // credential=null, which is indistinguishable from "none saved" and offers the capture
  // form; an operator typing into that form is rotating a credential that may exist. So
  // render nothing while the answer is outstanding, and say so when it never came.
  renderSavedState(credentialState, {
    companyId,
    utilityProviderId,
    propertyId,
    propertyUtilityId,
    utilityTypeId,
    credential,
    providerName,
    isAutomated,
    onChanged
  }) : renderPendingState(entitled, {
    dataRef: pendingRef,
    providerName,
    isAutomated
  }));
});